import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { LoadingSpinner } from "../../../shared/components/LoadingSpinner";
import { ModuleRegistry } from "ag-grid-community";
import { AllEnterpriseModule } from "ag-grid-enterprise";
import documentIcon from "../../../assets/images/documentIcon.svg";

ModuleRegistry.registerModules([AllEnterpriseModule]);

const BILLABLE_GROUP_SORT_ORDER = {
  Advised: 0,
  "Other Performing": 1,
  "Non-Performing": 2,
};

const CHILD_GROUP_SORT_ORDER = [
  "Cash and Equivalents",
  "Fixed Income",
  "Diversifying Strategies",
  "Opportunistic Credit",
  "Long/Short Equity",
  "Equities",
  "Real Assets",
  "Private Equity",
];

const getGroupFieldForNode = (node) => {
  const col = node?.rowGroupColumn;
  if (!col) return "";
  if (typeof col.getColId === "function") return String(col.getColId() || "");
  if (typeof col.getColDef === "function") {
    const def = col.getColDef();
    return String(def?.field || def?.colId || "");
  }
  return String(col?.colId || col?.colDef?.field || col?.colDef?.colId || "");
};

const getBillableSortOrder = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  if (Object.prototype.hasOwnProperty.call(BILLABLE_GROUP_SORT_ORDER, raw)) {
    return BILLABLE_GROUP_SORT_ORDER[raw];
  }
  const normalized = raw.toLowerCase();
  const matchKey = Object.keys(BILLABLE_GROUP_SORT_ORDER).find((key) => String(key).toLowerCase() === normalized);
  return matchKey ? BILLABLE_GROUP_SORT_ORDER[matchKey] : undefined;
};

const normalizeSortKey = (value) => String(value || "").trim().toLowerCase();
const CHILD_GROUP_SORT_INDEX = CHILD_GROUP_SORT_ORDER.reduce((acc, value, index) => {
  acc[normalizeSortKey(value)] = index;
  return acc;
}, {});

const isDirectChildOfBillableGroup = (node) =>
  Boolean(node?.parent) && getGroupFieldForNode(node.parent) === "BillableName";

const compareGroupValues = (valueA, valueB, nodeA, nodeB) => {
  const normalizedA = String(valueA || "").trim();
  const normalizedB = String(valueB || "").trim();
  const fieldA = getGroupFieldForNode(nodeA);
  const fieldB = getGroupFieldForNode(nodeB);
  const groupField = fieldA || fieldB;

  if (groupField === "BillableName") {
    const orderA = getBillableSortOrder(normalizedA);
    const orderB = getBillableSortOrder(normalizedB);

    if (orderA !== undefined || orderB !== undefined) {
      return (orderA ?? Number.MAX_SAFE_INTEGER) - (orderB ?? Number.MAX_SAFE_INTEGER);
    }
  }

  if (
    nodeA?.parent &&
    nodeB?.parent &&
    nodeA.parent === nodeB.parent &&
    isDirectChildOfBillableGroup(nodeA) &&
    isDirectChildOfBillableGroup(nodeB)
  ) {
    const orderA = CHILD_GROUP_SORT_INDEX[normalizeSortKey(normalizedA)];
    const orderB = CHILD_GROUP_SORT_INDEX[normalizeSortKey(normalizedB)];

    if (orderA !== undefined || orderB !== undefined) {
      return (orderA ?? Number.MAX_SAFE_INTEGER) - (orderB ?? Number.MAX_SAFE_INTEGER);
    }
  }

  return normalizedA.localeCompare(normalizedB);
};

const parseNumeric = (value) => {
  if (value == null) return NaN;
  if (typeof value === "number") return value;
  const cleaned = String(value).replace(/[$,%(),]/g, "").trim();
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const formatGridValue = (value, valueType, showDashForZero = false) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (valueType === "date") {
    const dt = new Date(value);
    if (Number.isFinite(dt.getTime())) {
      return dt.toLocaleDateString("en-US");
    }
    return String(value);
  }

  const numeric = parseNumeric(value);
  if (!Number.isFinite(numeric)) {
    return String(value);
  }

  if (showDashForZero && numeric === 0) {
    return "-";
  }

  if (valueType === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(numeric);
  }

  if (valueType === "percent") {
    return `${(numeric * 100).toFixed(2)}%`;
  }

  return numeric.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const sumAggFunc = (params) => {
  const total = (params.values || []).reduce((acc, value) => {
    const numeric = parseNumeric(value);
    return Number.isFinite(numeric) ? acc + numeric : acc;
  }, 0);
  return total;
};

const EXCEL_RIGHT_ALIGNED_STYLE_ID = "excelRightAligned";
const GRAND_TOTAL_LABEL = "Invested Capital Total";

const isGrandTotalNode = (node) =>
  Boolean(node) && (node.level === -1 || node.rowPinned === "bottom" || node.footer === true);

const toAgColumns = (columns = [], reportDate) =>
  columns.map((column) => {
    // Start of month (1st)
    const startOfMonth = new Date(reportDate.getFullYear(), reportDate.getMonth(), 1);
    // End of month (last day)
    const endOfMonth = new Date(reportDate.getFullYear(), reportDate.getMonth() + 1, 0);
    // Format as M/D
    const formatMD = (date) => `${date.getMonth() + 1}/${date.getDate()}`;

    if (column.columns?.length) {
      return {
        headerName: column.title === 'BMV'
                    ? `${column.title} (${formatMD(startOfMonth)})`
                    : column.title === 'EMV'
                    ? `${column.title} (${formatMD(endOfMonth)})`
                    : column.title,
        marryChildren: true,
        rowGroup: Boolean(column.rowGroup),
        hide: Boolean(column.hide),
        children: toAgColumns(column.columns),
      };
    }

    return {
      field: column.field,
      headerName: column.title === 'BMV'
                  ? `${column.title} (${formatMD(startOfMonth)})`
                  : column.title === 'EMV'
                  ? `${column.title} (${formatMD(endOfMonth)})`
                  : column.title,
      minWidth: column.width + 20,
      flex:1,
      rowGroup: Boolean(column.rowGroup),
      hide: Boolean(column.hide),
      pinned: column.pinned,
      aggFunc: column.isNumeric ? sumAggFunc : undefined,
      valueType: column.valueType,
      cellClass: column.isNumeric || column.valueType === "date" ? EXCEL_RIGHT_ALIGNED_STYLE_ID : undefined,
      sortable: true,
      resizable: true,
      valueFormatter: column.isNumeric || column.valueType === "date"
        ? (params) => formatGridValue(params.value, column.valueType, true)
        : undefined,
      cellRenderer: column.isNumeric || column.valueType === "date"
        ? (params) => formatGridValue(params.value, column.valueType, true)
        : undefined,
      cellStyle: column.isNumeric
        ? (params) => {
            const numeric = parseNumeric(params.value);
            return {
              textAlign: "right",
              color: Number.isFinite(numeric) && numeric < 0 ? "#c72c2c" : undefined,
            };
          }
        : undefined,
    };
  });

const HoldingsGrid = ({
  data,
  columns,
  loading,
  onExportTimeSeries,
  onViewDocument,
  reportDate
}) => {
  const gridRef = useRef(null);
  const tooltipHideTimerRef = useRef(null);
  const documentTooltipHideTimerRef = useRef(null);
  const [exportTooltip, setExportTooltip] = useState(null);
  const [documentTooltip, setDocumentTooltip] = useState(null);

  const columnDefs = useMemo(() => toAgColumns(columns, reportDate), [columns]);
  const excelStyles = useMemo(
    () => ([
      {
        id: EXCEL_RIGHT_ALIGNED_STYLE_ID,
        alignment: {
          horizontal: "Right",
        },
      },
    ]),
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: true,
    }),
    []
  );

  const getContextMenuItems = useCallback(
    (params) => {
      const items = ["copy", "copyWithHeaders", "separator", "export"];

      if (!params?.node?.group && params?.node?.data && typeof onExportTimeSeries === "function") {
        items.unshift({
          name: "Export Time Series",
          action: () => onExportTimeSeries(params.node.data),
        });
      }

      return items;
    },
    [onExportTimeSeries]
  );

  const handleExportExcel = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }

    if (typeof api.exportDataAsExcel === "function") {
      api.exportDataAsExcel({
        fileName: "Asset-Holdings.xlsx",
        sheetName: "Holdings",
        skipColumnGroupHeaders: false,
        processRowGroupCallback: (params) => {
          const label = String(params?.node?.key ?? params?.value ?? "").trim();
          const level = Math.max(Number(params?.node?.level ?? 0), 0);
          return `${"  ".repeat(level)}${label}`;
        },
        processCellCallback: (params) => {
          if (params?.column?.getColId?.() !== "ag-Grid-AutoColumn") {
            const colDef = typeof params?.column?.getColDef === "function" ? params.column.getColDef() : {};
            const valueType = colDef?.valueType;

            if (valueType === "currency" || valueType === "percent" || valueType === "date" || valueType === "number") {
              return formatGridValue(params.value, valueType, true);
            }

            return params.value;
          }

          const label = String(params?.node?.data?.fund ?? params?.value ?? "").trim();
          const nodeLevel = Number(params?.node?.level ?? 0);
          const level = Math.max(params?.node?.group ? nodeLevel : nodeLevel + 1, 0);
          return `${"  ".repeat(level)}${label}`;
        },
      });
      return;
    }

    api.exportDataAsCsv({ fileName: "holdings.csv" });
  }, []);

  const renderSecurityInner = useCallback(
    (params) => {
      if (isGrandTotalNode(params?.node)) {
        return <span>{GRAND_TOTAL_LABEL}</span>;
      }

      const canExport = Boolean(params?.node) && typeof onExportTimeSeries === "function";
      const docs = !params?.node?.group && Array.isArray(params?.data?.document?.DocumentList)
        ? params.data.document.DocumentList
        : [];

      const buildGroupPath = (node) => {
        const path = [];
        let current = node;
        while (current && current.level >= 0) {
          if (current.field) {
            path.unshift({ field: current.field, key: current.key });
          }
          current = current.parent;
        }
        return path;
      };

      const buildExportPayload = () => {
        if (params?.node?.level === -1) {
          return { __exportType: "portfolio" };
        }

        if (params?.node?.group) {
          return {
            __exportType: "group",
            groupPath: buildGroupPath(params.node),
            sampleData: params?.node?.allLeafChildren?.[0]?.data || null,
          };
        }

        return {
          __exportType: "security",
          rowData: params?.data || null,
        };
      };

      return (
        <span
          style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}
          onMouseEnter={(e) => {
            if (e?.target?.closest?.('[data-holdings-doc-icon="1"]')) return;
            if (!canExport) return;
            if (tooltipHideTimerRef.current) {
              clearTimeout(tooltipHideTimerRef.current);
              tooltipHideTimerRef.current = null;
            }
            const cellEl = e.currentTarget.closest(".ag-cell");
            const rect = (cellEl || e.currentTarget).getBoundingClientRect();
            setExportTooltip({
              rowId: params?.node?.id ?? null,
              payload: buildExportPayload(),
              x: rect.right,
              y: rect.top + 2,
            });
          }}
          onMouseLeave={() => {
            tooltipHideTimerRef.current = setTimeout(() => {
              setExportTooltip(null);
            }, 600);
          }}
        >
          {docs.length > 0 ? (
            <img
              src={documentIcon}
              alt="Documents"
              title="Documents"
              data-holdings-doc-icon="1"
              style={{ width: 14, height: 14, cursor: "pointer", flex: "0 0 auto", opacity: 0.9 }}
              onMouseEnter={(e) => {
                e.stopPropagation();
                if (documentTooltipHideTimerRef.current) {
                  clearTimeout(documentTooltipHideTimerRef.current);
                  documentTooltipHideTimerRef.current = null;
                }

                const rect = e.currentTarget.getBoundingClientRect();
                setDocumentTooltip({
                  x: rect.left,
                  y: rect.bottom + 6,
                  docs,
                  rowData: params.data,
                });
              }}
              onMouseLeave={() => {
                documentTooltipHideTimerRef.current = setTimeout(() => {
                  setDocumentTooltip(null);
                }, 350);
              }}
            />
          ) : null}
          <span>{params?.value}</span>
        </span>
      );
    },
    [onExportTimeSeries]
  );

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }

    if (loading) {
      api.showLoadingOverlay();
    } else if (data.length === 0) {
      api.showNoRowsOverlay();
    } else {
      api.hideOverlay();
    }
  }, [loading, data]);

  useEffect(() => () => {
    if (tooltipHideTimerRef.current) {
      clearTimeout(tooltipHideTimerRef.current);
      tooltipHideTimerRef.current = null;
    }
    if (documentTooltipHideTimerRef.current) {
      clearTimeout(documentTooltipHideTimerRef.current);
      documentTooltipHideTimerRef.current = null;
    }
  }, []);

  const handleGridReady = () => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }

    if (loading) {
      api.showLoadingOverlay();
    } else if (data.length === 0) {
      api.showNoRowsOverlay();
    } else {
      api.hideOverlay();
    }
  };

  if (loading) {
    return <div style={{ width: "100%", height: "calc(55vh - 160px)" }}><LoadingSpinner /></div>;
  }

  return (
    <div id="rightDiv" className="holdings-grid-panel">
      <div className="holdings-grid-toolbar">
        <button
          type="button"
          title="Export grid to Excel"
          className="holdings-excel-button"
          onClick={handleExportExcel}
          disabled={data.length === 0}
        >
          <span className="customizeexcelicon holdings-excel-icon" />
        </button>
      </div>

      <div className="ag-theme-alpine holdings-ag-grid" style={{ height: "calc(100vh - 160px)" }}>
        <AgGridReact
          ref={gridRef}
          onGridReady={handleGridReady}
          theme={"legacy"}
          rowData={data}
          columnDefs={columnDefs}
          getContextMenuItems={getContextMenuItems}
          defaultColDef={defaultColDef}
          excelStyles={excelStyles}
          suppressAggFuncInHeader={true}
          getRowClass={(params) => {
            if (isGrandTotalNode(params?.node)) {
              return "holdings-grand-total-row";
            }

            // Top-level BillableName groups
            if (
              params.node?.group &&
              getGroupFieldForNode(params.node) === "BillableName"
            ) {
              return "holdings-parent-group-row";
            }

            return "";
          }}
          groupDisplayType="singleColumn"
          groupDefaultExpanded={-1}
          grandTotalRow="bottom"
          autoGroupColumnDef={{
            headerName: "Security",
            minWidth: 330,
            pinned: "left",
            field: "fund",
            sort: "asc",
            comparator: compareGroupValues,
            cellRendererParams: {
              suppressCount: true,
              innerRenderer: renderSecurityInner,
              totalValueGetter: (params) => {
                if (isGrandTotalNode(params?.node)) {
                  return GRAND_TOTAL_LABEL;
                }
                return params?.value;
              },
            },
          }}
          overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No holdings data available</span>'}
          animateRows={false}
          suppressDragLeaveHidesColumns={true}
        />
      </div>
      {exportTooltip?.payload ? (
        <div
          style={{
            position: "fixed",
            left: exportTooltip.x,
            top: exportTooltip.y,
            background: "#10045a",
            color: "white",
            padding: "6px 10px",
            borderRadius: 4,
            whiteSpace: "nowrap",
            zIndex: 9999,
          }}
          onMouseEnter={() => {
            if (tooltipHideTimerRef.current) {
              clearTimeout(tooltipHideTimerRef.current);
              tooltipHideTimerRef.current = null;
            }
          }}
          onMouseLeave={() => setExportTooltip(null)}
        >
          <button
            type="button"
            className=""
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onExportTimeSeries(exportTooltip.payload);
              setExportTooltip(null);
            }}
            style={{
              color: "white",
              fontSize: 12,
              lineHeight: 1,
              textDecoration: "none",
              background: "transparent",
              border: "none",
              padding: 0,
              margin: 0,
              outline: "none",
              boxShadow: "none",
              cursor: "pointer",
            }}
          >
            Export Time Series
          </button>
        </div>
      ) : null}
      {Array.isArray(documentTooltip?.docs) && documentTooltip.docs.length > 0 ? (
        <div
          style={{
            position: "fixed",
            left: documentTooltip.x,
            top: documentTooltip.y,
            background: "white",
            color: "#111",
            padding: "8px 10px",
            borderRadius: 6,
            border: "1px solid rgba(0,0,0,0.15)",
            boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
            minWidth: 260,
            maxWidth: 420,
            zIndex: 9999,
          }}
          onMouseEnter={() => {
            if (documentTooltipHideTimerRef.current) {
              clearTimeout(documentTooltipHideTimerRef.current);
              documentTooltipHideTimerRef.current = null;
            }
          }}
          onMouseLeave={() => setDocumentTooltip(null)}
        >
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Documents</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {documentTooltip.docs.map((doc) => (
              <button
                key={doc?.DocumentId ?? doc?.documentId ?? `${doc?.DocumentName ?? doc?.documentName ?? "doc"}`}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (typeof onViewDocument === "function") {
                    onViewDocument(doc, documentTooltip.rowData);
                  }
                  setDocumentTooltip(null);
                }}
                style={{
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  color: "#10045a",
                  cursor: "pointer",
                  fontSize: 12,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={doc?.DocumentName ?? doc?.documentName ?? ""}
              >
                {doc?.DocumentName ?? doc?.documentName ?? "Document"}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default HoldingsGrid;
